/**
 * AJAX client for the SalaryLab calculation endpoint.
 * Posts to /tax-calculator/calculate and returns parsed JSON.
 * Includes an LRU result cache to eliminate duplicate requests.
 */

import { ResultCache } from "./result_cache"

const ENDPOINT = "/tax-calculator/calculate"
const MAX_SALARY = 1_000_000_000
const cache = new ResultCache(50)

/**
 * Perform a salary calculation via AJAX.
 * Returns cached results instantly when available.
 * @param {Object} params - Calculation parameters
 * @param {number} params.gross_salary
 * @param {string} [params.input_frequency="annual"]
 * @param {number} [params.hours_per_week=40]
 * @param {number} [params.kiwisaver_perc=0]
 * @param {boolean} [params.include_student_loan=false]
 * @param {string} [params.period_start_date]
 * @param {string} [params.secondary_tax_code]
 * @param {string} [params.type="paye"]
 * @returns {Promise<Object>} Calculation result
 */
export async function calculate(params) {
  const salaryFields = ["gross_salary", "rate", "target_net", "current_salary", "take_home"]
  for (const field of salaryFields) {
    if (params[field] && parseFloat(params[field]) > MAX_SALARY) {
      throw new Error("Salary values cannot exceed $1,000,000,000.")
    }
  }

  const cached = cache.get(params)
  if (cached) return cached

  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

  const response = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json",
      ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    },
    body: JSON.stringify(params),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Calculation failed" }))
    throw new Error(error.error || `HTTP ${response.status}`)
  }

  const result = await response.json()
  cache.set(params, result)
  return result
}
